import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckOutlined, ReloadOutlined, RightOutlined, WarningOutlined } from '@ant-design/icons';

import { Spinner } from '../../../../components/ui';
import { useDuongDan } from '../../../../hooks/useDuongDan';
import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { soVN } from '../../../../lib/dinh-dang';
import type { NodeTiming } from '../../types';

/**
 * Một chặng tiến trình, ở dạng component này cần để vẽ. Chữ ký mở: màn nào gửi thêm dữ liệu cho chặng cũng vẽ được.
 */
export interface Chang {
  step: string;
  detail: string;
  status: 'running' | 'ok' | 'warn' | 'error';
  /** Ô bản khai còn trống mà chặng nói tới — `unknown` vì component trình bày không nên biết bản sao là gì. */
  oCot?: unknown;
}

interface OCot {
  bang: string;
  cot: string;
  nguon?: string;
}

const oCotCua = (s: Chang): OCot[] => (Array.isArray(s.oCot) ? (s.oCot as OCot[]) : []);

/** Đường GỐC tới đúng ô khai báo còn trống của một bảng/cột (cùng luật ở `LuotHoi`). */
export function duongKhaiCot(o: OCot): string {
  const q = new URLSearchParams({ bang: o.bang, cot: o.cot }).toString();
  return o.nguon ? `/tro-ly-ai/ban-sao/${encodeURIComponent(o.nguon)}?${q}` : `/tro-ly-ai/ban-sao?${q}`;
}

export interface TienTrinhProps {
  steps: Chang[];
  nodes?: NodeTiming[];
  soLanGoiLlm?: number;
  model?: string;
  running: boolean;
  attempts?: number;
  /**
   * Số không quy về được ô hay đoạn tài liệu nào. RỖNG nghĩa là *mọi chữ số đều có nguồn*, KHÔNG phải *câu trả lời đã
   * được kiểm* — nên KHÔNG bao giờ vẽ một dấu tích "đã kiểm" khi danh sách rỗng.
   */
  khongNguon?: string[];
  /** Câu đúng với phần bảng mô hình nhìn thấy và sai với cả bảng — vẽ cùng chỗ, cùng màu với `khongNguon`. */
  cauVuotBang?: string[];
}

/**
 * Tiến trình của một lượt — một dòng chữ nhạt, không phải một cái hộp (port `components/ai/TienTrinh.tsx` native).
 *
 * LUÔN THU GỌN, chỉ mở khi bấm: bản tự bung trong lúc chạy đẩy câu trả lời xuống dưới màn hình. "Hệ thống còn sống" đã
 * đọc được ở nhãn nút (chặng đang chạy + spinner).
 *
 * Hai dòng nằm NGOÀI phần thu gọn — số lần tự viết lại câu lệnh và số con số chưa có nguồn: đó là tín hiệu TIN CẬY, giấu
 * đi thì một câu trả lời từng bị bác hai lần trông y hệt một câu chưa từng bị kiểm.
 */
export function TienTrinh({
  steps, nodes, soLanGoiLlm, model, running, attempts = 1, khongNguon = [], cauVuotBang = [],
}: TienTrinhProps) {
  const { t, locale } = useT();
  const duongDan = useDuongDan();
  const [open, setOpen] = useState(false);
  const rewrote = attempts > 1;

  // ── MẪU SỐ CỦA THANH TỈ LỆ KHÔNG PHẢI LÀ TỔNG ── ba nguồn của một lượt (ngữ cảnh · tri thức · web) chạy CHỒNG LẤN
  // và tự khai `songSong`. Cộng `ms` của chúng là đếm cùng một khoảng thời gian ba lần; phần chồng lấn tính bằng chặng
  // LÂU NHẤT.
  const tongMs = useMemo(() => {
    const ds = nodes ?? [];
    const noiDuoi = ds.filter((n) => !n.songSong).reduce((a, b) => a + b.ms, 0);
    const chongLan = ds.filter((n) => n.songSong).map((n) => n.ms);
    return noiDuoi + (chongLan.length ? Math.max(...chongLan) : 0) || 1;
  }, [nodes]);

  if (!steps.length && !running && !rewrote) return null;
  const last = steps[steps.length - 1];

  return (
    <div className="mt-1">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 text-xs text-content-muted transition-colors hover:text-content-primary"
      >
        <RightOutlined className={cn('text-xs transition-transform', open && 'rotate-90')} />
        {running ? (
          <>
            <Spinner size="small" />
            <span>{last?.detail ?? t('troLyAiHoiTienTrinh.dangBatDau')}</span>
          </>
        ) : (
          <>
            <CheckOutlined className="text-xs" />
            <span>
              {t(open ? 'troLyAiHoiTienTrinh.anTienTrinh' : 'troLyAiHoiTienTrinh.xemTienTrinh', {
                n: soVN(steps.length, locale),
              })}
            </span>
          </>
        )}
      </button>

      {!running && rewrote && (
        <p className="mt-1 flex items-start gap-1.5 text-xs font-medium text-success-700">
          <ReloadOutlined className="mt-0.5 shrink-0 text-xs" />
          {t('troLyAiHoiTienTrinh.daVietLai', { n: soVN(attempts - 1, locale) })}
        </p>
      )}
      {!running && khongNguon.length > 0 && (
        <p className="mt-1 flex items-start gap-1.5 text-xs font-medium text-warning-700">
          <WarningOutlined className="mt-0.5 shrink-0 text-xs" />
          {t('troLyAiHoiTienTrinh.soKhongNguon', { n: soVN(khongNguon.length, locale), ds: khongNguon.join(', ') })}
        </p>
      )}
      {!running && cauVuotBang.length > 0 && (
        <p className="mt-1 flex items-start gap-1.5 text-xs font-medium text-warning-700">
          <WarningOutlined className="mt-0.5 shrink-0 text-xs" />
          {t('troLyAiHoiTienTrinh.cauVuotBang', { n: soVN(cauVuotBang.length, locale), ds: cauVuotBang.join(' · ') })}
        </p>
      )}

      {open && (
        <ul className="mt-1.5 space-y-1 border-s-2 border-default ps-3">
          {steps.map((s, i) => (
            <li key={i} className="flex items-start gap-2 text-xs">
              <Mark status={s.status} last={i === steps.length - 1 && running} />
              <span className="w-[74px] shrink-0 text-content-secondary">{s.step}</span>
              <span
                className={
                  s.status === 'error'
                    ? 'text-danger-700'
                    : s.status === 'warn'
                      ? 'text-warning-700'
                      : 'text-content-secondary'
                }
              >
                {s.detail}
                {/* Chặng "chưa khai" nói đúng việc phải làm, nhưng việc ấy nằm ở màn khác, nguồn khác — không có đường
                    dẫn thì lời khuyên đúng cũng không ai theo. */}
                {oCotCua(s).map((o) => (
                  <Link
                    key={`${o.bang}.${o.cot}`}
                    to={duongDan(duongKhaiCot(o))}
                    className="ms-1.5 whitespace-nowrap font-medium underline underline-offset-2"
                  >
                    {t('troLyAiHoiTienTrinh.khaiCotNay', { cot: o.cot })}
                  </Link>
                ))}
              </span>
            </li>
          ))}

          {/* Bảng đo latency theo chặng. Không cộng dồn: một chặng chạy nhiều lượt (vòng tự sửa) hiện nhiều dòng — cộng
              lại là đọc `105s` thành một lời gọi chậm trong khi nó là 3 lượt × 35s. `LLM` đánh dấu chặng có gọi mô
              hình, chỉ số quyết định latency hơn mọi thứ khác. */}
          {nodes && nodes.length > 0 && (
            <li className="mt-2 border-t border-default pt-2 text-xs">
              <div className="mb-1 flex flex-wrap items-center gap-2 text-content-muted">
                <span className="font-medium">{t('troLyAiHoiTienTrinh.thoiGianChang')}</span>
                {soLanGoiLlm != null && (
                  <span>· {t('troLyAiHoiTienTrinh.loiGoiMoHinh', { n: soVN(soLanGoiLlm, locale) })}</span>
                )}
                {model && <span>· {model}</span>}
              </div>
              <div className="space-y-0.5 overflow-x-auto">
                {nodes.map((n, i) => (
                  <div key={i} className="flex min-w-max items-center gap-2">
                    <span className={cn('w-8 shrink-0 text-xs font-medium', n.llm ? 'text-warning-700' : 'text-content-muted')}>
                      {n.llm ? 'LLM' : ''}
                    </span>
                    <span className="w-[74px] shrink-0 text-content-secondary">
                      {n.node}
                      {/* Không có dấu này thì ba chặng chồng lấn đọc như ba chặng nối tiếp, và người đọc tự cộng lại. */}
                      {n.songSong && (
                        <span className="ms-1 text-content-muted" title={t('troLyAiHoiTienTrinh.chongLan')}>
                          ∥
                        </span>
                      )}
                    </span>
                    <span className="w-16 shrink-0 text-end tabular-nums text-content-primary">
                      {soVN(n.ms, locale)} ms
                    </span>
                    <span className="h-1.5 w-40 overflow-hidden rounded-full bg-subtle">
                      <span
                        className={cn('block h-full rounded-full', n.llm ? 'bg-warning-500' : 'bg-primary-300')}
                        style={{ width: `${Math.min(100, Math.max(1, (n.ms / tongMs) * 100))}%` }}
                      />
                    </span>
                    {/* Chặng đọc: lúc mẩu ĐẦU ra tới tai quan trọng hơn tổng của chính nó. */}
                    {n.msMauDau != null && (
                      <span className="w-[168px] shrink-0 text-end text-xs tabular-nums text-content-muted">
                        {t('troLyAiHoiTienTrinh.mauDau', { ms: soVN(n.msMauDau, locale) })}
                      </span>
                    )}
                    {n.tokensIn != null && (
                      <span className="w-[200px] shrink-0 text-end text-xs tabular-nums text-content-muted">
                        {t('troLyAiHoiTienTrinh.token', { vao: soVN(n.tokensIn, locale), ra: soVN(n.tokensOut ?? 0, locale) })}
                        {/* Đệm nguội là khoản đắt nhất một lượt (13.823 ms so 1.827 ms trên CÙNG câu hỏi) — không có cột
                            này thì lượt chậm vì vừa đồng bộ trông y hệt lượt chậm vì mô hình viết dài. */}
                        {n.cached != null && n.tokensIn > 0 && (
                          <span className={n.cached / n.tokensIn > 0.5 ? ' text-success-700' : ' font-medium text-warning-700'}>
                            {' '}
                            {t('troLyAiHoiTienTrinh.dem', { pt: Math.round((n.cached / n.tokensIn) * 100) })}
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

function Mark({ status, last }: { status: Chang['status']; last: boolean }) {
  if (status === 'running' || last) return <Spinner size="small" className="mt-0.5" />;
  if (status === 'error') return <WarningOutlined className="mt-0.5 shrink-0 text-xs text-danger-600" />;
  if (status === 'warn') return <ReloadOutlined className="mt-0.5 shrink-0 text-xs text-warning-600" />;
  return <CheckOutlined className="mt-0.5 shrink-0 text-xs text-success-600" />;
}
